import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Block, Inline } from '@contentful/rich-text-types';
import { Document } from '@contentful/rich-text-types';

import Text from '@/components/ui/Text';

const RichText = ({ richTextDocument }: RichTextProps) => {
  return (
    <div className="prose prose-lg max-w-none">
      {documentToReactComponents(richTextDocument, renderOptions)}
    </div>
  );
};

function toHeadingId(children: React.ReactNode): string {
  const text =
    typeof children === 'string'
      ? children
      : Array.isArray(children)
        ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
        : '';
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
}

const renderOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children: React.ReactNode) => (
      <Text as="p" variant="paragraph">
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_1]: (_node: Block | Inline, children: React.ReactNode) => (
      <Text
        as="h2"
        variant="sectionTitle"
        id={toHeadingId(children)}
        className="mb-6 mt-10 scroll-mt-[calc(var(--header-height)+2rem)]"
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_2]: (_node: Block | Inline, children: React.ReactNode) => (
      <Text
        as="h3"
        variant="sectionEyebrow"
        className="mb-6 mt-10 scroll-mt-[calc(var(--header-height)+2rem)]"
        id={toHeadingId(children)}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_3]: (_node: Block | Inline, children: React.ReactNode) => (
      <Text
        as="h4"
        variant="sectionEyebrow"
        className="mb-6 mt-10 scroll-mt-[calc(var(--header-height)+2rem)]"
        id={toHeadingId(children)}
      >
        {children}
      </Text>
    ),
    [BLOCKS.UL_LIST]: (_node: Block | Inline, children: React.ReactNode) => (
      <ul className="list-disc pl-6 space-y-2">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node: Block | Inline, children: React.ReactNode) => (
      <ol className="list-decimal pl-6 space-y-2">{children}</ol>
    ),
    [INLINES.HYPERLINK]: (node: Block | Inline, children: React.ReactNode) => {
      const href = (node.data as { uri?: string }).uri ?? '';
      const isExternal = href.startsWith('http');
      return (
        <a
          href={href}
          className="text-secondary underline underline-offset-2 hover:opacity-75 transition-opacity"
          {...(isExternal && {
            target: '_blank',
            rel: 'noopener noreferrer',
          })}
        >
          {children}
        </a>
      );
    },
  },
};

export default RichText;

type RichTextProps = {
  richTextDocument: Document;
};
